import React, { useCallback } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import PinLegendType from "@mapmycustomers/shared/enum/PinLegendType";
import { EntityTypesSupportedByMapsPage } from "@mapmycustomers/shared/types/entity";
import { Alert } from "@mapmycustomers/ui";

import ExtendedAnalytics from "@app/component/analytics/ExtendedAnalytics";
import MapMode from "@app/scene/map/enums/MapMode";
import { showCreatePinLegendDrawer } from "@app/scene/map/store/pinLegends/actions";
import { getMapMode } from "@app/scene/map/store/selectors";
import { RootState } from "@app/store/rootReducer";
import { MAP_ENTITY_TYPES } from "@app/util/map/consts";

import StandalonePopover from "../StandalonePopover";

import CommonPinSettings from "./components/CommonPinSettings";
import EntityPinSettings from "./components/EntityPinSettings";
import styles from "./PinSettingsPopover.module.scss";

const messages = defineMessages({
  pinSettings: {
    id: "map.toolbar.popovers.pinSettings",
    defaultMessage: "Pin Settings",
    description: "Title of the Pin Settings popover",
  },
  territoryModeWarning: {
    id: "map.toolbar.popovers.pinSettings.alert.territories",
    defaultMessage: "Changes made here will only apply in territory mode",
    description: "Alert shown in Pin Settings when map is in the Territory Mode",
  },
});

interface Props {
  onCreatePinLegend?: (payload: {
    entityType: EntityTypesSupportedByMapsPage;
    type: PinLegendType;
  }) => void;
  onHide?: () => void;
  showTerritoryModeWarning?: boolean;
}

const PinSettingsPopover: React.FC<Props> = ({
  onCreatePinLegend,
  onHide,
  showTerritoryModeWarning,
}) => {
  const intl = useIntl();

  const handleShowNewColorLegendDrawer = useCallback(
    (entityType: EntityTypesSupportedByMapsPage) => {
      onCreatePinLegend?.({ entityType, type: PinLegendType.COLOR });
      onHide?.();
    },
    [onCreatePinLegend, onHide]
  );

  const handleShowNewShapeLegendDrawer = useCallback(
    (entityType: EntityTypesSupportedByMapsPage) => {
      onCreatePinLegend?.({ entityType, type: PinLegendType.SHAPE });
      onHide?.();
    },
    [onCreatePinLegend, onHide]
  );

  return (
    <StandalonePopover
      contentClassName={styles.container}
      title={intl.formatMessage(messages.pinSettings)}
    >
      <ExtendedAnalytics keyPath="Pin Settings">
        {showTerritoryModeWarning && (
          <Alert message={intl.formatMessage(messages.territoryModeWarning)} showIcon type="info" />
        )}
        <CommonPinSettings />
        {MAP_ENTITY_TYPES.map((entityType) => (
          <EntityPinSettings
            entityType={entityType}
            key={entityType}
            onCreateCustomColorLegend={handleShowNewColorLegendDrawer}
            onCreateCustomShapeLegend={handleShowNewShapeLegendDrawer}
          />
        ))}
      </ExtendedAnalytics>
    </StandalonePopover>
  );
};

const mapStateToProps = (state: RootState) => ({
  showTerritoryModeWarning: getMapMode(state) === MapMode.TERRITORIES,
});

const mapDispatchToProps = {
  onCreatePinLegend: showCreatePinLegendDrawer,
};

export default connect(mapStateToProps, mapDispatchToProps)(PinSettingsPopover);
